import React from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const ComparisonChart = ({ 
  onUpgradeClick, 
  currentPlan = 'free',
  showCTAButton = true,
  className = ''
}) => {
  const features = [
    {
      category: 'Assessment & Planning',
      items: [
        {
          name: 'Career Assessment',
          free: 'Basic assessment (1 per month)',
          premium: '3 assessments per month (included)',
          icon: 'ClipboardList'
        },
        {
          name: 'AI Career Plans',
          free: '1 AI-generated plan/mo; short-term goals; basic steps',
          premium: 'Unlimited, fully customized plans (short/medium/long term)',
          icon: 'Zap'
        },
        {
          name: 'Industry-Specific Insights',
          free: 'General insights only',
          premium: 'Deep industry analysis & trends',
          icon: 'TrendingUp'
        }
      ]
    },
    {
      category: 'Skills & Development',
      items: [
        {
          name: 'Skill Exercises',
          free: 'Limited basic exercises',
          premium: 'Advanced unlimited exercises',
          icon: 'BookOpen'
        },
        {
          name: 'Progress Tracking',
          free: 'Basic progress metrics',
          premium: 'Advanced analytics & insights',
          icon: 'BarChart3'
        },
        {
          name: 'Plan Scope & Goals',
          free: 'Short-term only (2–4 weeks)',
          premium: '30/60/90-day and long-term milestones',
          icon: 'Target'
        }
      ]
    },
    {
      category: 'Salary & Career Advancement',
      items: [
        {
          name: 'Salary Insights',
          free: 'Basic salary ranges',
          premium: 'Detailed ZIP code salary data',
          icon: 'DollarSign'
        },
        {
          name: 'Negotiation Scenarios',
          free: false,
          premium: 'AI-powered negotiation practice',
          icon: 'MessageSquare'
        },
        {
          name: 'Career Advancement Probability',
          free: false,
          premium: 'Data-driven advancement predictions',
          icon: 'ArrowUpRight'
        }
      ]
    },
    {
      category: 'Support & Resources',
      items: [
        {
          name: 'Resume Content Kit',
          free: false,
          premium: 'AI-optimized content & templates',
          icon: 'FileText'
        },
        {
          name: 'Support Access',
          free: 'Community support only',
          premium: 'Priority email support',
          icon: 'HeadphonesIcon'
        },
        {
          name: '1-on-1 Coaching',
          free: false,
          premium: 'Monthly expert sessions',
          icon: 'Users'
        }
      ]
    }
  ];

  const pricing = {
    free: { price: 0, period: 'forever' },
    premium: { price: 19.99, period: 'month' }
  };

  return (
    <div className={`bg-card border border-border rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 px-6 py-1 border-b border-border">
        <div className="flex items-center justify-center space-x-2">
          <Icon name="GitCompare" size={24} className="text-primary" />
          <h2 className="text-xl font-bold text-foreground">Plan Comparison</h2>
        </div>
        <p className="text-center text-muted-foreground text-sm mt-2">
          See what's included with each subscription tier
        </p>
      </div>
      {/* Plan Headers */}
      <div className="grid grid-cols-3 border-b border-border">
        <div className="p-4">
          <div className="text-center">
            <h3 className="font-semibold text-foreground text-sm">Features</h3>
          </div>
        </div>
        
        {/* Free Plan Header */}
        <div className={`p-4 border-l border-border ${currentPlan === 'free' ? 'bg-success/5 ring-1 ring-success/20' : ''}`}>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 mb-2">
              <Icon name="Star" size={16} className="text-muted-foreground" />
              <h3 className="font-semibold text-foreground">Free</h3>
              {currentPlan === 'free' && (
                <div className="ml-2 px-2 py-0.5 bg-success text-success-foreground text-xs rounded-full">
                  Current
                </div>
              )}
            </div>
            <div className="text-2xl font-bold text-foreground">
              ${pricing?.free?.price}
            </div>
            <div className="text-xs text-muted-foreground">
              /{pricing?.free?.period}
            </div>
          </div>
        </div>

        {/* Premium Plan Header */}
        <div className={`p-4 border-l border-border ${currentPlan === 'premium' ? 'bg-success/5 ring-1 ring-success/20' : 'bg-warning/10'}`}>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1 mb-2">
              <Icon name="Crown" size={16} className="text-warning" />
              <h3 className="font-semibold text-foreground">Premium</h3>
              {currentPlan === 'premium' && (
                <div className="ml-2 px-2 py-0.5 bg-success text-success-foreground text-xs rounded-full">
                  Current
                </div>
              )}
            </div>
            <div className="text-2xl font-bold text-foreground">
              ${pricing?.premium?.price}
            </div>
            <div className="text-xs text-muted-foreground">
              /{pricing?.premium?.period}
            </div>
          </div>
        </div>
      </div>
      {/* Feature Categories */}
      <div className="divide-y divide-border">
        {features?.map((category, categoryIndex) => (
          <div key={categoryIndex}>
            {/* Category Header */}
            <div className="bg-muted/30 px-4 py-3">
              <h4 className="font-semibold text-sm text-foreground">
                {category?.category}
              </h4>
            </div>
            
            {/* Category Features */}
            {category?.items?.map((feature, featureIndex) => (
              <div key={featureIndex} className="grid grid-cols-3 hover:bg-muted/20 transition-colors">
                {/* Feature Name */}
                <div className="p-4 flex items-center space-x-3">
                  <Icon 
                    name={feature?.icon} 
                    size={16} 
                    className="text-muted-foreground flex-shrink-0" 
                  />
                  <span className="text-sm text-foreground font-medium">
                    {feature?.name}
                  </span>
                </div>

                {/* Free Column */}
                <div className="p-4 border-l border-border flex items-center">
                  {feature?.free === false ? (
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <Icon name="X" size={16} className="text-red-500" />
                      <span className="text-sm">Not included</span>
                    </div>
                  ) : (
                    <div className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">
                        {feature?.free}
                      </span>
                    </div>
                  )}
                </div>

                {/* Premium Column */}
                <div className="p-4 border-l border-border flex items-center">
                  <div className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">
                      {feature?.premium}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      {/* Bottom CTA Section */}
      {showCTAButton && currentPlan !== 'premium' && (
        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 px-6 py-1 border-t border-border">
          <div className="grid grid-cols-3 gap-4 items-center">
            <div></div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-2">
                Perfect for getting started
              </p>
            </div>
            <div className="text-center">
              <Button
                onClick={onUpgradeClick}
                variant="default"
                size="sm"
                className="w-full"
                iconName="Crown"
                iconPosition="left"
              >
                Upgrade to Premium
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                30-day money-back guarantee
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Current Premium User Message */}
      {currentPlan === 'premium' && (
        <div className="bg-success/10 border-t border-success/20 px-6 py-1">
          <div className="flex items-center justify-center space-x-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm text-success font-medium">
              You're enjoying all Premium features!
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonChart;